/* eslint-disable react/jsx-props-no-spreading */
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Modal} from 'components/ui';
import {Button, PasswordInput} from 'letrus-ui';
import {useForm} from 'react-hook-form';

import {
  resetPasswordRequest,
  updateUserProfileRequest,
} from 'APIs/letrusAPI/user';
import {ResetPassword} from 'APIs/letrusAPI/user/types';
import {useCallback, useEffect, useState} from 'react';
import {
  getUserData,
  updateUserData,
} from 'utils/functions/getUserData/getUserData';
import styles from './ResetPasswordModal.module.scss';

const passwordRecommendations = [
  'Ela deve ter no mínimo 6 caracteres.',
  'Ela deve conter um ou mais números.',
  'Ela deve conter tanto caracteres maiúsculos quanto minúsculos.',
];

export interface ResetPasswordModalProps {
  onModalOpen?(): void;
  onModalClose?(): void;
}

function ResetPasswordModal({
  onModalOpen,
  onModalClose,
}: ResetPasswordModalProps = {}) {
  const [isOpen, setIsOpen] = useState(false);

  const userData = getUserData();
  const resetPassword = resetPasswordRequest();
  const updateProfile = updateUserProfileRequest();

  const {formState, ...form} = useForm<ResetPassword>({mode: 'onBlur'});

  useEffect(() => {
    if (
      userData.user?.temp_password &&
      userData?.userProfile?.flag_show_change_password
    ) {
      setIsOpen(true);
      onModalOpen?.();
    }
  }, []);

  function handleSubmit(data: ResetPassword) {
    resetPassword.trigger(
      {...data, userId: userData.user?.id},
      {
        onSuccess: updateLocalUserData,
        onError: (error) => updateFormErrors(error.message, error.cause),
      },
    );
  }

  function updateLocalUserData() {
    setIsOpen(false);
    onModalClose?.();

    updateUserData('temp_password', false);
    updateUserData('flag_show_change_password', false);
  }

  function updateFormErrors(field: string, message: unknown) {
    form.setError(field as keyof ResetPassword, {message: message as string});
  }

  const validatePassword = useCallback(
    (value: string, isConfirmation?: boolean) => {
      if (value?.length < 6) {
        return 'Deve ter no mínimo 6 caracteres.';
      }

      if (value?.match(/[0-9]/) === null) {
        return 'Deve conter pelo menos um número.';
      }
      if (value?.match(/[A-Z]/) === null) {
        return 'Deve conter pelo menos uma letra maiúscula.';
      }
      if (value?.match(/[a-z]/) === null) {
        return 'Deve conter pelo menos uma letra minúscula.';
      }

      if (isConfirmation && value !== form.getValues('new_password')) {
        return 'Confirmação não corresponde à nova senha.';
      }

      return true;
    },
    [],
  );

  return (
    <Modal
      isOpen={isOpen}
      buttonClose
      onClose={() => {
        updateLocalUserData();
        updateProfile.trigger({
          id: userData?.user?.user_profile.id,
          flag_show_change_password: false,
        });
      }}
      clickOutsideToClose={false}
    >
      <div className={styles.wrapper} data-testid="new-password-modal">
        <div className={styles.message}>
          <h2 className={styles.title}>Ano novo, senha nova 😝</h2>

          <p>
            Certifique-se que irá criar uma senha forte, mas que também que você
            lembrará no futuro.
          </p>

          <ul>
            {passwordRecommendations.map((recommendation) => (
              <li className={styles.recommendation} key={recommendation}>
                <FontAwesomeIcon icon="check" size="1x" /> {recommendation}
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <PasswordInput
            label="Senha atual"
            placeholder="Senha"
            errorMessage={formState.errors.current_password?.message}
            {...form.register('current_password')}
          />

          <PasswordInput
            label="Nova senha"
            placeholder="Senha"
            onKeyUp={() => form.clearErrors('new_password_re')}
            errorMessage={formState.errors.new_password?.message}
            {...form.register('new_password', {validate: validatePassword})}
          />

          <PasswordInput
            label="Confirmar senha"
            placeholder="Senha"
            errorMessage={formState.errors.new_password_re?.message}
            {...form.register('new_password_re', {
              validate: (value) => validatePassword(value, true),
            })}
          />

          <Button
            type="submit"
            loading={resetPassword.isMutating}
            disabled={resetPassword.isMutating}
          >
            Salvar
          </Button>
        </form>
      </div>
    </Modal>
  );
}

export default ResetPasswordModal;
